import React, {useState, useContext, ReactNode, useEffect} from 'react';
import {DndContext, closestCenter, useSensors, useSensor, PointerSensor} from '@dnd-kit/core';
import {arrayMove, SortableContext, useSortable, horizontalListSortingStrategy} from '@dnd-kit/sortable';
import {CSS} from '@dnd-kit/utilities';
import 'cropperjs/dist/cropper.css';
import {Box, Button, TextField, Typography, IconButton, Rating, Tooltip, Grid2, CircularProgress} from '@mui/material';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import DeleteIcon from '@mui/icons-material/Delete';
import ReviewContext from "../contexts/ReviewContext.tsx";
import {useTranslation} from "react-i18next";
import {IReview} from "../models/IReview.ts";

const VITE_API_BASE_URL = import.meta.env.VITE_API_BASE_URL

interface ReviewFormProps {
    orderId: string;
    onReviewSubmit: (e: any) => void;
    existingReview?: IReview | null;
}

const SortableItem = ({id, children}: { id: string; children: ReactNode }) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
    } = useSortable({id})

    const style = {
        transform: CSS.Transform.toString(transform),
        touchAction: 'none',
        transition,
    }

    return (
        <Grid2 ref={setNodeRef} style={style} {...attributes} {...listeners}>
            {children}
        </Grid2>
    )
}

const ReviewForm: React.FC<ReviewFormProps> = ({orderId, onReviewSubmit, existingReview}) => {
    const {t} = useTranslation();
    const {
        createReviewMutation,
        updateReviewMutation,
        errorCreatingReview,
        errorUpdatingReview,
        addImageMutation,
        deleteImageMutation,
        addImageError
    } = useContext(ReviewContext);
    const [rating, setRating] = useState<number>(0);
    const [comment, setComment] = useState<string>('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [croppedImages, setCroppedImages] = useState<Array<{
        file: File | null;
        url: string;
        existing?: boolean
    }>>([]);
    const imagesToDelete: string[] = []


    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                delay: 10,
                tolerance: 5,
            },
        })
    );

    const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!e.target.files) return;
        const newFiles = Array.from(e.target.files);

        const filePreviews = newFiles.map((file) => ({
            file,
            url: URL.createObjectURL(file),
        }));

        setCroppedImages((prev) => [...prev, ...filePreviews]);
    };

    const handleRemoveImage = async (index: number) => {
        if (croppedImages[index].file === null) imagesToDelete.push(croppedImages[index].url)
        setCroppedImages(croppedImages.filter((_, i) => i !== index));
    };

    const handleDragEnd = ({active, over}: any) => {
        if (active.id !== over.id) {
            const oldIndex = croppedImages.findIndex((img) => img.url === active.id);
            const newIndex = croppedImages.findIndex((img) => img.url === over.id);
            setCroppedImages(arrayMove(croppedImages, oldIndex, newIndex));
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSubmitting(true);

        let uploadedImageUrls: string[] = [];

        const newImages = croppedImages.filter(img => img.file !== null);
        const existingImageUrls = croppedImages.filter(img => img.file === null).map(img => img.url);

        if (newImages.length > 0) {
            const imageData = new FormData();
            newImages.forEach((image) => {
                imageData.append('images', image.file!);
            });

            const response = await addImageMutation.mutateAsync(imageData);
            uploadedImageUrls = response.imageUrls;
        }

        const reviewData: IReview = {
            order: orderId,
            rating,
            comment,
            images: [...existingImageUrls, ...uploadedImageUrls],
        };

        if (imagesToDelete.length > 0) {
            imagesToDelete.map(async (image) => {
                await deleteImageMutation.mutateAsync(image.replace('/uploads/', '').replace('..', '').replace(`${VITE_API_BASE_URL}/`, ''));
            })
        }

        if (existingReview) {
            reviewData._id = existingReview._id;
            await updateReviewMutation.mutateAsync({id: existingReview._id, ...reviewData});
            setIsSubmitting(false);
        } else {
            createReviewMutation.mutate(reviewData);
            setIsSubmitting(false);
        }

        setRating(0);
        setComment('');
        setCroppedImages([]);
        onReviewSubmit(e);
    };
    useEffect(() => {
        if (existingReview) {
            setRating(existingReview.rating);
            setComment(existingReview.comment);
            if (existingReview.images && existingReview.images.length > 0) {
                const filePreviews = existingReview.images.map((url) => ({
                    file: null,
                    url: url,
                    existing: true
                }));
                setCroppedImages(filePreviews);
            }
        }
    }, [existingReview]);

    return (
        <Box
            component="form"
            onSubmit={handleSubmit}
            p={3}
            sx={{maxWidth: 500, margin: 'auto'}}
            onClick={(e) => e.stopPropagation()}
        >
            <Typography variant="h6">{t('REVIEW_TEXT')}</Typography>
            <Rating value={rating} onChange={(_, newValue) => setRating(newValue!)}/>

            <TextField
                label={t('COMMENT_LABEL_TEXT')}
                fullWidth
                multiline
                rows={3}
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                margin="normal"
            />

            {croppedImages && (
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                    <SortableContext items={croppedImages.map((img: any) => img.url)}
                                     strategy={horizontalListSortingStrategy}>

                        <Grid2 container>
                            {croppedImages.map((image: any, index: any) => (
                                <Grid2 key={image.url} size={{xs: 6, md: 4}}>
                                    <Box sx={{position: 'relative', padding: 1}}>
                                        <SortableItem id={image.url}>
                                            <Box
                                                sx={{
                                                    width: '100%',
                                                    aspectRatio: '1 / 1',
                                                    borderRadius: '8px',
                                                    overflow: 'hidden',
                                                    backgroundColor: '#f5f5f5',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                }}
                                            >
                                                <img
                                                    src={image.existing ? `${VITE_API_BASE_URL}/${image.url}` : image.url}
                                                    alt={`Bild ${index + 1}`}
                                                    style={{
                                                        width: '100%',
                                                        height: '100%',
                                                        objectFit: 'cover',
                                                        display: 'block',
                                                    }}
                                                />
                                            </Box>
                                            <Tooltip title={t("DELETE_IMAGE")}>
                                                <IconButton
                                                    size="small"
                                                    color="error"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleRemoveImage(index)
                                                    }}
                                                    sx={{position: 'absolute', top: 4, right: 4, margin: 1}}
                                                >
                                                    <DeleteIcon/>
                                                </IconButton>
                                            </Tooltip>
                                        </SortableItem>

                                    </Box>
                                    {/*{deleteImageError &&*/}
                                    {/*    <Typography>*/}
                                    {/*        {t('DELETE_IMAGE_ERROR_TEXT')}*/}
                                    {/*    </Typography>*/}
                                    {/*}*/}
                                </Grid2>
                            ))}
                        </Grid2>
                    </SortableContext>
                </DndContext>
            )}

            <Button
                variant="outlined"
                startIcon={<AddPhotoAlternateIcon/>}
                component="label"
                fullWidth
                sx={{mt: 2}}
            >
                {t('ADD_IMAGE_BUTTON_TEXT')}
                <input
                    type="file"
                    hidden
                    accept="image/*"
                    multiple
                    onChange={handleImageChange}
                />
            </Button>

            <Button
                type="submit"
                variant="contained"
                disabled={isSubmitting}
                fullWidth
                sx={{mt: 2}}
                onClick={(e) => e.stopPropagation()}
            >
                {!isSubmitting ? t('REVIEW_TEXT') : <CircularProgress size={24}/>}
            </Button>
            {errorCreatingReview || addImageError &&
                <Typography color="error">{t('ERROR_CREATING_REVIEW')}</Typography>}
            {errorUpdatingReview || addImageError &&
                <Typography color="error">{t('ERROR_UPDATING_REVIEW')}</Typography>}
        </Box>
    );
};

export default ReviewForm;
